/**
 * Box 5: spend share per channel (spec §3.3, R15). One channel is "100% X"
 * with no chart; channels with no spend are listed below, never drawn as 0%.
 * The legend is HTML (the PieTileChart pattern), so shares are readable text.
 *
 * @module components/overview/ChannelDonut
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { TOOLTIP_PROPS } from '@/components/looker/charts/chartTheme';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { colorAt } from '@/components/charts/chartPalette';
import { platformLabel } from '@/utils/platformLabel';
import { fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import type { ChannelShare } from '@/services/overview/channelShare';

const DONUT_HEIGHT = 200;

export const ChannelDonut: React.FC<{ share: ChannelShare; currency: string | null }> = ({ share, currency }) => {
  const { t } = useTranslation();
  const noSpend = share.noSpend.length > 0
    ? <p className="mt-2 text-center text-xs text-neutral-700">{t('overview.donut.noSpend', { list: share.noSpend.map(platformLabel).join(', ') })}</p>
    : null;
  // M3: rows but no spend > 0 anywhere → the empty-box line and the list, never a blank chart.
  if (share.slices.length === 0) {
    return (
      <div>
        <BoxMessage text={t('looker.tileEmpty')} />
        {noSpend}
      </div>
    );
  }
  if (share.slices.length === 1) {
    return (
      <div className="py-6 text-center">
        <p className="text-2xl font-semibold text-ink-950">{t('overview.donut.only', { channel: platformLabel(share.slices[0].platform) })}</p>
        <p className="mt-1 text-sm tabular-nums text-neutral-800">{fmtMoney(share.slices[0].spend, currency)}</p>
        {noSpend}
      </div>
    );
  }
  return (
    <div>
      <div style={{ height: DONUT_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={share.slices} dataKey="spend" nameKey="platform" innerRadius="55%" outerRadius="85%" isAnimationActive={false}>
              {share.slices.map((s, i) => <Cell key={s.platform} fill={colorAt(i)} />)}
            </Pie>
            <Tooltip {...TOOLTIP_PROPS} formatter={(v: unknown) => fmtMoney(typeof v === 'number' ? v : null, currency)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {share.slices.map((s, i) => (
          <li key={s.platform} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-neutral-900">
              <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorAt(i) }} />
              {platformLabel(s.platform)}
            </span>
            <span className="tabular-nums text-neutral-800">{fmtPct(s.share, 1)}</span>
          </li>
        ))}
      </ul>
      {noSpend}
    </div>
  );
};
